import { Search, Plus, History, Settings, Minimize2, Maximize2, X, ArrowLeft } from 'lucide-react'
import { memo } from 'react'
import { SyncStatusIndicator } from '../../../../components/SyncStatusIndicator'
import { useSync } from '../../../../contexts/SyncContext'
import type { PanelState, PanelActions } from '../../types/PanelTypes'

interface PanelHeaderProps {
    state: PanelState
    actions: PanelActions
    refs?: {
        searchButton?: React.RefObject<HTMLButtonElement | null>
    }
}

/**
 * Industrial Panel Header (Shell Isolation)
 */
export const PanelHeader = memo(({ state, actions, refs }: PanelHeaderProps) => {
    // Selective Subscription for Sync
    const { syncStatus, syncMessage } = useSync()

    // Shell State
    const { isFullscreen, canGoBack, view } = state
    const {
        goBack,
        openAgentSearch,
        startNewChat,
        setView,
        toggleFullscreen,
        closePanel
    } = actions

    return (
        <div className="w-full shrink-0 bg-white dark:bg-black z-[100] relative">
            <div className={`flex h-12 items-center justify-between px-3 ${!isFullscreen ? 'border-b border-gray-200 dark:border-gray-800' : ''} ${isFullscreen ? 'max-w-4xl mx-auto' : ''}`}>
                <div className="flex items-center gap-1 justify-end w-full">
                    {/* Navigation: Back or Main Controls */}
                    {canGoBack ? (
                        <button
                            onClick={goBack}
                            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded transition-colors flex items-center gap-2 mr-auto"
                        >
                            <ArrowLeft className="w-6 h-6 lg:w-4 lg:h-4 text-gray-600 dark:text-gray-400" />
                            <span className="text-sm font-medium text-gray-600 dark:text-gray-400">Voltar</span>
                        </button>
                    ) : (
                        <>
                            <button
                                ref={refs?.searchButton}
                                onClick={openAgentSearch}
                                className={`p-2 rounded transition-colors flex ${view === 'search'
                                    ? 'bg-blue-50 text-blue-600 dark:bg-blue-900/50 dark:text-blue-400'
                                    : 'hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-600 dark:text-gray-400'
                                    }`}
                                title="Buscar agentes"
                            >
                                <Search className="w-6 h-6 lg:w-4 lg:h-4" />
                            </button>
                            <button
                                onClick={startNewChat}
                                className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded transition-colors flex"
                                title="Nova conversa"
                            >
                                <Plus className="w-6 h-6 lg:w-4 lg:h-4 text-gray-600 dark:text-gray-400" />
                            </button>
                            <button
                                onClick={() => setView('history')}
                                className={`p-2 rounded transition-colors flex ${view === 'history'
                                    ? 'bg-blue-50 text-blue-600 dark:bg-blue-900/50 dark:text-blue-400'
                                    : 'hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-600 dark:text-gray-400'
                                    }`}
                                title="Histórico"
                            >
                                <History className="w-6 h-6 lg:w-4 lg:h-4" />
                            </button>
                            <button
                                onClick={() => setView('settings')}
                                className={`p-2 rounded transition-colors flex ${view === 'settings'
                                    ? 'bg-blue-50 text-blue-600 dark:bg-blue-900/50 dark:text-blue-400'
                                    : 'hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-600 dark:text-gray-400'
                                    }`}
                                title="Configurações"
                            >
                                <Settings className="w-6 h-6 lg:w-4 lg:h-4" />
                            </button>
                        </>
                    )}

                    {/* Sync Status - selective subcription prevents master re-render */}
                    {!canGoBack && (
                        <div className="flex">
                            <SyncStatusIndicator
                                status={syncStatus === 'syncing' ? 'syncing' : 'synced'}
                                message={syncMessage || ''}
                            />
                        </div>
                    )}

                    {/* Window Controls */}
                    <button
                        onClick={toggleFullscreen}
                        className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded transition-colors block"
                        title={isFullscreen ? "Restaurar" : "Maximizar"}
                    >
                        {isFullscreen ? (
                            <Minimize2 className="w-6 h-6 lg:w-4 lg:h-4 text-gray-600 dark:text-gray-400" />
                        ) : (
                            <Maximize2 className="w-6 h-6 lg:w-4 lg:h-4 text-gray-600 dark:text-gray-400" />
                        )}
                    </button>
                    <button
                        onClick={closePanel}
                        className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded transition-colors block"
                        title="Fechar"
                    >
                        <X className="w-6 h-6 lg:w-4 lg:h-4 text-gray-600 dark:text-gray-400" />
                    </button>
                </div>
            </div>
        </div>
    )
})
